/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {MathHelper} from "@nolimitcity/slot-game/bin/core/utils/MathHelper";
import {BitmapWinCountUp} from "@nolimitcity/slot-game/bin/game/winpresentation/countup/BitmapWinCountUp";
import {Elastic, Power2, Power3, Timeline, TimelineLite, TweenLite} from "gsap";
import {TombstoneSoundConfig} from "../../TombstoneSoundConfig";
import {TombstoneGameConfig} from "../../TombstoneGameConfig";
import {TombstoneGameModuleConfig} from "../../TombstoneGameModuleConfig";
import {TombstoneNoMercy} from "../../TombstoneNoMercy";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";

export class TombstoneInitialWinCountUp extends BitmapWinCountUp {

    private _hasCountUpEndSoundPlayed:boolean = false;
    private _hasMultiplierEndSoundPlayed:boolean = false;
    private _isShowing:boolean = false;
    private _endValue:number;
    private _endMultiplier:number;
    private _belowStakeWinRestriction:boolean = false;
    private _winRatio:number;

    public setWinRatio(winRatio:number, belowStackWinRestriction:boolean) {
        this._winRatio = winRatio;
        this._belowStakeWinRestriction = belowStackWinRestriction;
    }

    constructor(style?: {font:any, tint: any}) {
        super((style)? style : {
            font : {name : "NumbersTombstone", size : 46},
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.onBeforeSpinStart());
        TombstoneNoMercy.api.events.on("removeCountUpForAS", (betData: APIFeatureBetEventData) => {
            this.removeCountUpForAS();
        })
    }

    private getScale(multiplier:number):number {
        return 1 + (multiplier - 1) * 0.1;
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number):Timeline {
        this._endValue = to;
        this._endMultiplier = multiplier;
        const oriNumber:number = multiplier === 1? this._endValue: MathHelper.floorToDecimals(to / multiplier, 2);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
                if(duration > 0.01) {
                    if(!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
                        SlotGame.sound.playEffect(TombstoneSoundConfig.instance.COUNT_UP, -1);
                    }
                }
                this._isShowing = true;
            },
         (!TombstoneGameModuleConfig.gameData.isWinBelowStake())? this._countUp.getCountUpAnimation(from, oriNumber, {duration : duration, ease : Power3.easeInOut}) : () => {}
        ]);

        timeline.add(() => {this.stopCountUpSound();});
        if(TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
            timeline.add(this.getEndingAnimation(this._endValue), duration + 0.5);
        }

        if(multiplier > 1) {
            const levelTime:number = 0.5 * multiplier > 5 ? MathHelper.roundToDecimals(5 / multiplier, 2) : 0.5;
            for(let m:number = 1; m <= multiplier; m++) {
                const showNumber:number = oriNumber * m;
                const scale:number = this.getScale(m);
                if(m > 1) {
                    if(m === multiplier) {
                        timeline.add(() => {
                            // #277

                            if(!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
                                SlotGame.sound.playEffect(TombstoneSoundConfig.instance.COUNT_UP_ENDS);
                            }

                            this._hasMultiplierEndSoundPlayed = true;
                        });
                    } // #277
                    else if (!TombstoneGameConfig.instance.WIN_BELOW_STAKE) {
                        if(!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
                            timeline.add(() => SlotGame.sound.playEffect(TombstoneSoundConfig.instance.MULTIPLIER_WIN));
                        }
                    }
                }
                timeline.add([
                    this._countUp.getCountUpAnimation(showNumber, showNumber, {duration : 0.01}),
                    TweenLite.to(this._countUpContainer.scale, levelTime / 2,
                        {
                            x : scale,
                            y : scale,
                            ease : Elastic.easeOut.config(2, 1)
                        }),
                ]);
                timeline.add(TweenLite.to(this, levelTime / 2, {}));
            }
        }


        return timeline;
    }

    private stopCountUpSound():void {
        SlotGame.sound.stopEffect(TombstoneSoundConfig.instance.COUNT_UP);

        if(!this._hasCountUpEndSoundPlayed) {
            // #277
            if (!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
                SlotGame.sound.playEffect(TombstoneSoundConfig.instance.COUNT_UP_ENDS);
            }
            this._hasCountUpEndSoundPlayed = true;
        }
    }

    public abort(endValue:number, isAborted:boolean, multiplier:number, isBigWin:boolean):Timeline | null {
        if(isBigWin) {
            return null;
        }

        this._endValue = endValue;
        this._endMultiplier = multiplier;
        const timeline:TimelineLite = new TimelineLite();
        if(isAborted) {
            timeline.add(this.getAbortAnimation(endValue, multiplier));
        }
        timeline.add(this.stay());
        return timeline;
    }

    protected getAbortAnimation(endValue:number, multiplier:number):Timeline {
        this._isShowing = true;
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            () => {
                this._countUp.maxTextWidth = GameConfig.instance.REEL_AREA_WIDTH * 0.9 / this.getScale(multiplier);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                this._countUpContainer.scale.set(1, 1);
            },
            this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01})
        ]);
        if(multiplier > 1) {
            const scale:number = this.getScale(multiplier);
            timeline.add(TweenLite.to(this._countUpContainer.scale, 0.2, {
                x : scale,
                y : scale,
                ease : Elastic.easeOut.config(2, 1)
            }), 0);
        }

        return timeline
    }

    public getEndingAnimation(endValue:number):Timeline {
        this._isShowing = false;
        const timeline:TimelineLite = new TimelineLite({
            onComplete : () => {
                this._hasCountUpEndSoundPlayed = false;
                this._hasMultiplierEndSoundPlayed = false;
            }
        });
        timeline.add(this._countUp.getCountUpAnimation(endValue, endValue, {duration : 0.01}));
        timeline.add([
            TweenLite.to(this._countUp, 0.5, {alpha : 0, ease : Power2.easeIn}),
            TweenLite.to(this._countUpContainer, 0.5, {y : -100, ease : Power2.easeIn})
        ], `+=0.8`); // stay a while after count up.
        timeline.add(() => this._countUpContainer.y = 0);

        return timeline;
    }

    private removeCountUpForAS(){
        if(this._countUp){
            this._countUp.alpha=0;
        }
    }

    public stay():Timeline {
        const timeline:TimelineLite = new TimelineLite();
        timeline.add(() => {
            if(this._endMultiplier > 1 && !this._hasMultiplierEndSoundPlayed) {
                // #277
                if (!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
                    SlotGame.sound.playEffect(TombstoneSoundConfig.instance.COUNT_UP_ENDS);
                }

                this._hasMultiplierEndSoundPlayed = true;
                this._hasCountUpEndSoundPlayed = true;
            }
            this.stopCountUpSound();
        });

        // #277
        if (!TombstoneGameModuleConfig.gameData.isWinBelowStake()) {
            timeline.add(() => SlotGame.sound.playEffect(TombstoneSoundConfig.instance.COUNT_UP_BACK));
        }

        timeline.add(TweenLite.to(this._countUpContainer.scale, 0.9, {
            x : 0.7,
            y : 0.7,
            ease : Elastic.easeOut.config(2, 0.75)
        }));
        return timeline;
    }

    private onBeforeSpinStart():void {
        if(this._isShowing) {
            this.getEndingAnimation(this._endValue);
        }
    }
}